/**
 * @file alert-dialog.tsx
 */

"use client";

import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "visuais/fmt";
import { buttonVariants } from "./button";

/**
 * @returns {JSX.Element} Diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialog>
 *   <AlertDialogTrigger>Trigger</AlertDialogTrigger>
 *   <AlertDialogContent>
 *     <AlertDialogHeader>
 *       <AlertDialogTitle>Título</AlertDialogTitle>
 *       <AlertDialogDescription>Descrição</AlertDialogDescription>
 *     </AlertDialogHeader>
 *     <AlertDialogFooter>
 *       <AlertDialogCancel>Cancelar</AlertDialogCancel>
 *       <AlertDialogAction>Ação</AlertDialogAction>
 *     </AlertDialogFooter>
 *   </AlertDialogContent>
 * </AlertDialog>
 * ```
 */
export function AlertDialog({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

/**
 * @returns {JSX.Element} Gatilho do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogTrigger>Trigger</AlertDialogTrigger>
 * ```
 */
export function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  );
}

/**
 * @returns {JSX.Element} Portal do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogPortal>
 *   <AlertDialogOverlay />
 *   <AlertDialogContent />
 * </AlertDialogPortal>
 * ```
 */
export function AlertDialogPortal({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
  );
}

/**
 * @returns {JSX.Element} Overlay do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogOverlay />
 * ```
 */
export function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Conteúdo do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogContent>
 *   <AlertDialogHeader>
 *     <AlertDialogTitle>Título</AlertDialogTitle>
 *     <AlertDialogDescription>Descrição</AlertDialogDescription>
 *   </AlertDialogHeader>
 *   <AlertDialogFooter>
 *     <AlertDialogCancel>Cancelar</AlertDialogCancel>
 *     <AlertDialogAction>Ação</AlertDialogAction>
 *   </AlertDialogFooter>
 * </AlertDialogContent>
 * ```
 */
export function AlertDialogContent({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
}

/**
 * @returns {JSX.Element} Cabeçalho do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogHeader>
 *   <AlertDialogTitle>Título</AlertDialogTitle>
 *   <AlertDialogDescription>Descrição</AlertDialogDescription>
 * </AlertDialogHeader>
 * ```
 */
export function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Rodapé do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogFooter>
 *   <AlertDialogCancel>Cancelar</AlertDialogCancel>
 *   <AlertDialogAction>Ação</AlertDialogAction>
 * </AlertDialogFooter>
 * ```
 */
export function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Título do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogTitle>Título</AlertDialogTitle>
 * ```
 */
export function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn("text-lg font-semibold", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Descrição do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogDescription>Descrição</AlertDialogDescription>
 * ```
 */
export function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Ação do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogAction>Ação</AlertDialogAction>
 * ```
 */
export function AlertDialogAction({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action>) {
  return (
    <AlertDialogPrimitive.Action
      className={cn(buttonVariants(), className)}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Cancelamento do diálogo de alerta.
 * @example
 * ```tsx
 * <AlertDialogCancel>Cancelar</AlertDialogCancel>
 * ```
 */
export function AlertDialogCancel({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel>) {
  return (
    <AlertDialogPrimitive.Cancel
      className={cn(buttonVariants({ variant: "outline" }), className)}
      {...props}
    />
  );
}
